"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { LexicalText } from "@/schema"
import { SerializedEditorState } from "lexical"
import { Loader2 } from "lucide-react"

import { useReplacePlaceholders } from "@/hooks/api/email-templates"
import { useFetchHousehold } from "@/hooks/api/households/queries"
import type { EmailRecipientsConfig } from "@/components/automations/types"
import EmailBodyEditor from "@/components/email/body-editor"
import type { EmailEditorProvider } from "@/components/lexical/editors/email/provider-styles"
import PeekView from "@/components/peek-view"

import { BulkEmailRecipientData } from "./recipient-item"

const RECIPIENT_SUBJECT_TYPES = { household: "Household", contact: "Contact", person: "Person" } as const

interface SerializedLexicalNode {
  type: string
  children?: SerializedLexicalNode[]
  [key: string]: unknown
}

function convertPlaceholdersToText(node: SerializedLexicalNode): SerializedLexicalNode {
  if (node.type === "placeholder") {
    return {
      ...node,
      type: "text",
    }
  }

  if (node.children) {
    return {
      ...node,
      children: node.children.map(convertPlaceholdersToText),
    }
  }

  return node
}

function processReplacedNodes(state: SerializedEditorState): SerializedEditorState {
  return {
    ...state,
    root: convertPlaceholdersToText(state.root) as SerializedEditorState["root"],
  }
}

interface ReviewEmailPreviewProps {
  recipient: BulkEmailRecipientData
  emailIntegrationId: string
  subject: string
  body: LexicalText
  roleMapping?: EmailRecipientsConfig
  provider?: EmailEditorProvider
}

interface RecipientEmail {
  name: string
  email: string
}

export function ReviewEmailPreview({
  recipient,
  emailIntegrationId,
  subject,
  body,
  roleMapping,
  provider,
}: ReviewEmailPreviewProps) {
  const { mutateAsync } = useReplacePlaceholders()
  const [renderedSubject, setRenderedSubject] = useState<string | null>(null)
  const [renderedBody, setRenderedBody] = useState<LexicalText | null>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const mutateRef = useRef(mutateAsync)
  mutateRef.current = mutateAsync

  const isHousehold = recipient.type === "household"
  const { data: householdData } = useFetchHousehold(recipient.id, { enabled: isHousehold })

  const { toRecipients, ccRecipients, bccRecipients } = useMemo(() => {
    const to: RecipientEmail[] = []
    const cc: RecipientEmail[] = []
    const bcc: RecipientEmail[] = []

    if (!isHousehold || !householdData) {
      if (recipient.email) {
        to.push({ name: recipient.name, email: recipient.email })
      }
      return { toRecipients: to, ccRecipients: cc, bccRecipients: bcc }
    }

    const household = householdData
    const mapping = roleMapping ?? { head_of_household: "to", spouse_or_partner: "none" }

    const addPerson = (
      person: {
        full_name?: string | null
        primary_email_address?: { email: string } | null
        email?: string | null
      } | null,
      role: "to" | "cc" | "bcc" | "none" | undefined
    ) => {
      if (!person || !role || role === "none") return
      const email = person.primary_email_address?.email ?? person.email
      if (!email) return

      const entry = { name: person.full_name ?? "Unknown", email }
      if (role === "to") to.push(entry)
      else if (role === "cc") cc.push(entry)
      else if (role === "bcc") bcc.push(entry)
    }

    addPerson(household.head_of_household, mapping.head_of_household)
    addPerson(household.spouse, mapping.spouse_or_partner)
    addPerson(household.partner, mapping.spouse_or_partner)

    return { toRecipients: to, ccRecipients: cc, bccRecipients: bcc }
  }, [isHousehold, householdData, roleMapping, recipient.email, recipient.name])

  useEffect(() => {
    setRenderedSubject(null)
    setRenderedBody(null)

    let cancelled = false
    const subjectType = RECIPIENT_SUBJECT_TYPES[recipient.type]

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const subjectResult = await mutateRef.current({
          content: subject,
          subjectType,
          emailIntegrationId,
          subjectId: recipient.id,
        })

        const bodyResult = await mutateRef.current({
          content: body.nodes ? JSON.stringify(body.nodes) : (body.html ?? ""),
          subjectType,
          emailIntegrationId,
          subjectId: recipient.id,
          format: body.nodes ? "nodes" : "html",
        })

        if (cancelled) return
        setRenderedSubject(subjectResult.content ?? "")
        setRenderedBody(
          body.nodes
            ? { nodes: processReplacedNodes(JSON.parse(bodyResult.content) as SerializedEditorState) }
            : { html: bodyResult.content ?? "" }
        )
      } catch {
        if (cancelled) return
        setRenderedSubject(subject)
        setRenderedBody(body)
      }
    }, 300)

    return () => {
      cancelled = true
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [subject, body, recipient.id, recipient.type, emailIntegrationId])

  if (renderedSubject === null || renderedBody === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    )
  }

  const formatRecipients = (recipients: RecipientEmail[]) => recipients.map((r) => r.email).join(", ") || "(none)"

  return (
    <div className="relative flex h-full flex-col">
      <div className="bg-primary/5 pointer-events-none absolute inset-0 z-10" />
      <p className="flex items-center gap-1 border-b px-4 py-1 text-sm font-medium">
        <span className="">Previewing email for</span>
        <span className="relative z-20 pt-0.5">
          <PeekView
            recordType={RECIPIENT_SUBJECT_TYPES[recipient.type]}
            recordId={recipient.id}
            label={recipient.name}
            size="sm"
          />
        </span>
      </p>

      <div className="flex items-center border-b px-4 py-2">
        <span className="text-tertiary-foreground w-10 flex-none text-sm font-medium">To</span>
        <span className="text-foreground text-sm">{formatRecipients(toRecipients)}</span>
      </div>

      {ccRecipients.length > 0 && (
        <div className="flex items-center border-b px-4 py-2">
          <span className="text-tertiary-foreground w-10 flex-none text-sm font-medium">Cc</span>
          <span className="text-foreground text-sm">{formatRecipients(ccRecipients)}</span>
        </div>
      )}

      {bccRecipients.length > 0 && (
        <div className="flex items-center border-b px-4 py-2">
          <span className="text-tertiary-foreground w-10 flex-none text-sm font-medium">Bcc</span>
          <span className="text-foreground text-sm">{formatRecipients(bccRecipients)}</span>
        </div>
      )}

      <div className="flex items-center border-b px-4 py-2">
        <span className="text-tertiary-foreground w-10 flex-none text-sm font-medium">Subj</span>
        <span className="text-foreground text-sm">{renderedSubject || "(No subject)"}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <EmailBodyEditor
          initialContent={renderedBody}
          provider={provider}
          editable={false}
          placeholder=""
          styleOverrides={{ "--lexical-editor-min-height": "280px" }}
        />
      </div>
    </div>
  )
}
